/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useRef, useState } from "react";
import { ConnectionQuality, RoomEvent, type Room } from "livekit-client";

import styles from "./InCallView.module.css";

type Level = "excellent" | "good" | "fair" | "poor" | "lost";

interface NetworkStats {
  uploadMbps: number;
  downloadMbps: number;
  pingMs: number;
  level: Level;
}

function levelFromQuality(q: ConnectionQuality): Level {
  switch (q) {
    case ConnectionQuality.Excellent:
      return "excellent";
    case ConnectionQuality.Good:
      return "good";
    case ConnectionQuality.Poor:
      return "poor";
    default:
      return "lost";
  }
}

function barsFor(level: Level): number {
  switch (level) {
    case "excellent":
      return 4;
    case "good":
      return 3;
    case "fair":
      return 2;
    case "poor":
      return 1;
    default:
      return 0;
  }
}

function getPc(room: Room): RTCPeerConnection | undefined {
  // The publisher transport carries the uplink RTCPeerConnection.
  const engine = (room as unknown as { engine?: { publisher?: { pc?: RTCPeerConnection } } })
    .engine;
  return engine?.publisher?.pc;
}

export const NetworkQualityIndicator: FC<{ room: Room }> = ({ room }) => {
  const [stats, setStats] = useState<NetworkStats>({
    uploadMbps: 0,
    downloadMbps: 0,
    pingMs: 0,
    level: "lost",
  });
  const last = useRef<{ bytesSent: number; bytesRecv: number; ts: number }>({
    bytesSent: 0,
    bytesRecv: 0,
    ts: 0,
  });

  useEffect(() => {
    const updateLevel = (q: ConnectionQuality): void => {
      setStats((s) => ({ ...s, level: levelFromQuality(q) }));
    };
    room.on(RoomEvent.ConnectionQualityChanged, updateLevel);
    updateLevel(room.localParticipant.connectionQuality);

    let cancelled = false;
    const poll = async (): Promise<void> => {
      const pc = getPc(room);
      if (!pc) return;
      let report: RTCStatsReport;
      try {
        report = await pc.getStats();
      } catch {
        return;
      }
      if (cancelled) return;

      let bytesSent = 0;
      let bytesRecv = 0;
      let pingMs = 0;
      let hasPair = false;
      report.forEach((s) => {
        if (s.type === "outbound-rtp") {
          bytesSent += s.bytesSent ?? 0;
        } else if (s.type === "inbound-rtp") {
          bytesRecv += s.bytesReceived ?? 0;
        } else if (
          s.type === "candidate-pair" &&
          (s as { state?: string }).state === "succeeded" &&
          (s as { nominated?: boolean }).nominated
        ) {
          hasPair = true;
          const rtt = (s as { currentRoundTripTime?: number })
            .currentRoundTripTime;
          if (typeof rtt === "number") pingMs = Math.round(rtt * 1000);
        }
      });

      const now = performance.now();
      const prev = last.current;
      if (prev.ts > 0) {
        const dt = (now - prev.ts) / 1000;
        if (dt > 0) {
          const uploadMbps = ((bytesSent - prev.bytesSent) * 8) / 1e6 / dt;
          const downloadMbps = ((bytesRecv - prev.bytesRecv) * 8) / 1e6 / dt;
          setStats((s) => ({
            ...s,
            uploadMbps: Math.max(0, uploadMbps),
            downloadMbps: Math.max(0, downloadMbps),
            pingMs: hasPair ? pingMs : s.pingMs,
          }));
        }
      }
      last.current = { bytesSent, bytesRecv, ts: now };
    };

    const id = window.setInterval(() => void poll(), 2000);
    void poll();

    return () => {
      cancelled = true;
      window.clearInterval(id);
      room.off(RoomEvent.ConnectionQualityChanged, updateLevel);
    };
  }, [room]);

  const bars = barsFor(stats.level);
  const label =
    stats.level === "lost"
      ? "No connection"
      : stats.level.charAt(0).toUpperCase() + stats.level.slice(1);

  return (
    <div className={styles.network} title="Your connection quality">
      <div className={styles.networkBars} data-level={stats.level}>
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={styles.networkBar}
            data-active={i < bars}
          />
        ))}
      </div>
      <div className={styles.networkText}>
        <span className={styles.networkLabel}>
          My quality: {label}
        </span>
        <span className={styles.networkSpeed}>
          {stats.pingMs > 0
            ? `Ping: ${stats.pingMs}ms`
            : "Ping: —"}
          {" · "}{stats.uploadMbps.toFixed(1)} 
          {stats.downloadMbps.toFixed(1)} Mb
        </span>
      </div>
    </div>
  );
};
